import { WSActions, WSAlert, WSList } from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import {
  queriesPayerEngagements,
  queriesPayerRows,
  queriesPayers,
  queriesWorkItems,
  queriesWorkLogs
} from "@query";
import { Layout } from "../../../../shared/components/Layout";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { CardWorkItems } from "../../components/CardWorkItems";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { WorkLogEngagementDetails } from "../../components/WorkLogEngagementDetails";
import { useModalConvert } from "../../modals/ModalConvert";
import { useModalExit } from "./ModalExit";

export const RouteEdit: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const history = useHistory();
  const workLogId = props.match.params.workLogId;
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // WorkLog's readonly `payerEngagementId` is the counterpart PayerEngagement
  // id (distinct from its own `payeeEngagementId`) — search rows by it to
  // resolve the payer id the engagement read below is scoped to.
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: {
        engagements_id: {
          eq: workLog?.payerEngagementId ?? undefined
        }
      }
    },
    {
      enabled: !!workLog?.payerEngagementId
    }
  );

  const payerRow = queryPayerRow.data?.data?.[0];
  const payerId = payerRow?.id;
  const payerPayeeEngagementId = workLog?.payerEngagementId ?? undefined;

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );
  // The counterpart engagement: its `workDefinitions` drive the work-item form
  // and it is the resource the dynamic work-item enum lookups read
  // (CardWorkItems' `resources.payerPayeeEngagement`).
  const queryPayerEngagement =
    queriesPayerEngagements.getPayerEngagement.useQuery(
      { payerId: payerId!, payerEngagementId: payerPayeeEngagementId! },
      { enabled: !!payerId && !!payerPayeeEngagementId }
    );
  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerEngagement.data;

  const workDefinitions = queryPayerEngagement.data?.workDefinitions ?? [];

  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId) {
      return "Log Work";
    }

    if (!payerRow) {
      return "Log Work";
    }

    const payerName = selectorSearchRowName(payerRow);

    if (payerPayeeEngagement) {
      return `Log Work – ${payerName} • ${payerPayeeEngagement.engagementName}`;
    }

    return `Log Work – ${payerName}`;
  }, [payerRow, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: { workLogId: { eq: workLogId } }
  });

  const modalExit = useModalExit();
  const modalConvert = useModalConvert();

  const [createError, setCreateError] = useState(false);

  const isInvoiceCreated = !!workLog?.convertedResourceId;

  return (
    <Layout
      title="Work log"
      onClose={() => {
        modalExit.open();
      }}
    >
      <WSList gap="2XL">
        <WorkLogEngagementDetails
          shimmer={queryPayerRow.isInitialLoading}
          payer={payer}
          payerPayeeEngagement={payerPayeeEngagement}
        />

        <CardWorkItems
          shimmer={
            queryWorkItems.isInitialLoading ||
            queryPayerEngagement.isInitialLoading
          }
          noEdit={isInvoiceCreated}
          workLogId={workLogId}
          payerId={payerId!}
          payerPayeeEngagementId={payerPayeeEngagementId!}
          workItems={queryWorkItems.data?.data || []}
          workDefinitions={workDefinitions}
          resources={{
            payerPayeeEngagement
          }}
        />

        {createError && (
          <WSAlert
            title="Invoice creation error"
            theme="warning"
            icon="alert"
            onDismiss={() => {
              setCreateError(false);
            }}
          >
            There was an error creating the invoice. Please try again. If the
            issue persists, please contact support.
          </WSAlert>
        )}

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: isInvoiceCreated ? "View invoice" : "Create invoice",
              disabled:
                filterActiveWorkItems(queryWorkItems.data?.data || [])
                  .length === 0,
              onClick: () => {
                if (isInvoiceCreated) {
                  history.push(
                    `/member/invoices/${workLog.convertedResourceId}`
                  );
                } else {
                  modalConvert.open({
                    workLogId,
                    onClose: result => {
                      setCreateError(result?.createError || false);
                    }
                  });
                }
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
